import { useEffect, useRef, useState } from 'react';
import { trackRumAction } from '@/shared/observability/rum';
import {
  DiscoverySource,
  recordProviderProfileView,
} from '../api/profileViewApi';

export const useProviderProfileView = (
  providerId?: string,
  searchContextId?: string,
  discoverySource: DiscoverySource = 'DIRECT_PROFILE',
): string | null => {
  // Keyed by providerId (not a Set) so switching back to an already-recorded
  // provider — the route doesn't remount on an `:id` param change alone —
  // restores *that* provider's id instead of leaving a sibling provider's
  // stale id in place, which the backend would then reject as a
  // provider-mismatch and silently fail to mark "converted to billable".
  const recordedIds = useRef(new Map<string, string | null>());
  const [engagementEventId, setEngagementEventId] = useState<string | null>(null);

  useEffect(() => {
    if (!providerId) {
      setEngagementEventId(null);
      return;
    }

    if (recordedIds.current.has(providerId)) {
      setEngagementEventId(recordedIds.current.get(providerId) ?? null);
      return;
    }

    // Never attribute a conversion to a different provider's profile view
    // while this provider's own record call is still in flight.
    setEngagementEventId(null);
    let active = true;

    recordProviderProfileView(providerId, searchContextId, discoverySource)
      .then((result) => {
        recordedIds.current.set(providerId, result.engagementEventId);
        if (active) setEngagementEventId(result.engagementEventId);
        trackRumAction('provider_profile_view', {
          providerId,
          discoverySource,
          created: result.created,
        });
      })
      .catch(() => {
        // Left unrecorded so a later effect run for this providerId can retry.
      });

    return () => {
      active = false;
    };
  }, [providerId, searchContextId, discoverySource]);

  return engagementEventId;
};
